import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import PartialTextWithIcon from '../partials/twb_text_with_icon';
import PartialText from '../partials/twb_text';
import PartialFeatures from '../partials/twb_features';
import TwbCTA from '../partials/twb_cta';
import PartialColumnItems from '../partials/twb_column_items';

import PartialCTA from '../partials/cta';
import {
  cn,
  getMobileImagePlacement,
  getTitleSizeStyle,
} from '../../../utils/helpers';
import { useViewport } from '~/utils/viewport-provider';
import Checkmarks from '../checkmarks/checkmarks';

type ResponsiveTitleSize = {
  desktop: number;
  mobile: number;
};
interface TextWithImageProps {
  block: {
    settings: Settings;
    rounded_corners: string;
    pre_title: string;
    title: string;
    title_mobile: string;
    title_size: ResponsiveTitleSize;
    pre_title_size: ResponsiveTitleSize;
    text_background_color: string;
    title_font_size: number;
    text_font_size: number;
    text: string;
    text_type: string;
    padding_top: number;
    padding_bottom: number;
    image_position: string;
    hide_image_on_mobile: boolean;
    image_alt_text: string;
    image: string;
    cta_field: any;
    mobile_image_placement: {
      placement: number;
      width: number;
    };
    rounded_corners_image: string;
    image_width: {
      desktop: number;
      mobile: number;
    };
    text_image_block_width: {
      desktop: number;
      mobile: number;
    };
    text_with_blocks: {
      blocks: {
        blocks: any[];
        [key: string]: any;
      }[];
    }[];
    css_class_name: string;
    background_color: string;
    style_class: string;
    background: any;
    // legacy parent might mutated the block object
    image_position_class: string;
  };
}

const TextWithImage: React.FC<TextWithImageProps> = ({ block }) => {
  const { viewport } = useViewport();
  const { image_width, text_type, text_image_block_width } = block;

  let textColumnSize = `col-xl-${text_image_block_width?.desktop || '6'} col-md-${text_image_block_width?.mobile || '7'}`;
  let imageColumnSize = 'col-xl-6 col-md-5';
  let imagePositionClass = block.image_position_class;
  let textType = '';
  if (image_width) {
    const textColumnDesktop = 12 - image_width.desktop;
    const textColumnMobile = 12 - image_width.mobile;
    imageColumnSize = `col-xl-${image_width.desktop} col-md-${image_width.mobile}`;
    textColumnSize = `col-xl-${text_image_block_width?.desktop || textColumnDesktop} col-md-${text_image_block_width?.mobile || textColumnMobile}`;
  }

  if (text_type) {
    textType = `text-formatted--${text_type}`;
  }

  if (!imagePositionClass) {
    // I am not confident enought to ignore this rule.
    // There might be a weird case where image_position_class is set in props.
    if (block.image_position === 'Left') {
      imagePositionClass = 'img-left';
    } else if (block.image_position === 'Right') {
      imagePositionClass = 'img-right';
    } else {
      imagePositionClass = 'img-center';
    }
  }

  const titleSize = getTitleSizeStyle(block.title_size);
  // This is the only way to get the mobile title size to work
  // when title_size.mobile value is not being passed to the component.
  const titleMobile = block.title_mobile
    ? block.title_mobile
    : titleSize.mobile
      ? block.title
      : null;

  const blockStateClass = cn(
    `${block.css_class_name ? block.css_class_name : ''} ${block.background_color ? 'has-background' : ''} ${imagePositionClass} ${block.style_class ? block.style_class : ''} ${block.hide_image_on_mobile ? ' img-hidden-on-mobile' : ''}`
  );

  const mobileImagePlacement = getMobileImagePlacement(
    block.mobile_image_placement
  );

  const textBackgroundColor = {
    background: block.text_background_color,
  };

  const ROUNDED_FROM_TWI_CSS = 'rounded-[6px]';

  return (
    <section className={`block__text_with_image block ${blockStateClass}`}>
      {block.background}
      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div
              className={`content-wr ${block.rounded_corners ? block.rounded_corners : ROUNDED_FROM_TWI_CSS}`}
              style={{
                background: block.background_color
                  ? block.background_color
                  : 'transparent',
                paddingTop: block.padding_top ? `${block.padding_top}rem` : '',
                paddingBottom: block.padding_bottom
                  ? `${block.padding_bottom}rem`
                  : '',
              }}
            >
              <div className='row'>
                <div
                  className={`text border-rounded-strong md:pt-8 ${textColumnSize}`}
                  style={textBackgroundColor}
                >
                  <Pretitle
                    text={block.pre_title}
                    size={block.pre_title_size}
                    device={viewport.device}
                  />
                  {titleMobile ? (
                    <>
                      <h2
                        className={`is-desktop`}
                        dangerouslySetInnerHTML={{ __html: block.title }}
                        style={titleSize.desktop}
                      ></h2>
                      <h2
                        className={`is-mobile`}
                        dangerouslySetInnerHTML={{ __html: titleMobile }}
                        style={titleSize.mobile}
                      ></h2>
                    </>
                  ) : (
                    block.title && (
                      <h2
                        dangerouslySetInnerHTML={{ __html: block.title }}
                        style={titleSize.desktop}
                      ></h2>
                    )
                  )}

                  {block.image && block.title && (
                    <div
                      className={`is-mobile ${imageColumnSize} ${imagePositionClass}`}
                    >
                      <img
                        className='card-image'
                        src={block.image}
                        alt={block.image_alt_text}
                        title={block.image_alt_text}
                        loading='lazy'
                        style={mobileImagePlacement}
                      />
                    </div>
                  )}
                  {block.text && (
                    <div
                      className={`text-formatted ${textType}`}
                      dangerouslySetInnerHTML={{ __html: block.text }}
                    ></div>
                  )}

                  {block.text_with_blocks &&
                    block.text_with_blocks.map((twb: any, i) => {
                      let nested = twb.blocks.map(
                        (blockPartial: any, j: string) => {
                          switch (blockPartial._template) {
                            case 'partial__text':
                              return (
                                <PartialText
                                  key={`partial-${i}-${j}`}
                                  block={blockPartial}
                                  image={block.image}
                                  image_alt_text={block.image_alt_text}
                                />
                              );
                            case 'partial__text_with_icon':
                              return (
                                <PartialTextWithIcon
                                  key={`partial-${i}-${j}`}
                                  block={blockPartial}
                                />
                              );
                            case 'partial__features':
                              return (
                                <PartialFeatures
                                  key={`partial-${i}-${j}`}
                                  block={blockPartial}
                                />
                              );
                            case 'partial__cta':
                              return (
                                <TwbCTA
                                  key={`partial-${i}-${j}`}
                                  block={blockPartial}
                                />
                              );
                            case 'partial__column_items':
                              return (
                                <PartialColumnItems
                                  key={`partial-${i}-${j}`}
                                  block={blockPartial}
                                />
                              );
                            case 'block__checkmarks':
                              return (
                                <Checkmarks
                                  key={`partial-${i}-${j}`}
                                  marginTop={blockPartial.margin_top}
                                  marginBottom={blockPartial.margin_bottom}
                                  alignLeft={blockPartial.checkmarks_align_left}
                                  checkmarks={blockPartial.checkmarks || []}
                                  gap={blockPartial.gap}
                                  columns_bootstrap_rule={
                                    blockPartial.columns_bootstrap_rule
                                  }
                                />
                              );
                            default:
                              return 'Missing block.';
                          }
                        }
                      );
                      return (
                        <React.Fragment key={`partial-${i}`}>
                          {nested}
                        </React.Fragment>
                      );
                    })}

                  {block.cta_field && <PartialCTA data={block.cta_field} />}
                </div>
                <div className={`image is-desktop ${imageColumnSize}`}>
                  {block.image && (
                    <img
                      src={block.image}
                      alt={block.image_alt_text}
                      title={block.image_alt_text}
                      loading='lazy'
                      className={`${block.rounded_corners_image ? block.rounded_corners_image : ''}`}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pretitle = ({
  text,
  size,
  device,
}: {
  text: string;
  size: ResponsiveTitleSize;
  device: string;
}) => {
  const [style, setStyle] = useState({});
  if (!text) {
    return null;
  }

  useEffect(() => {
    if (size) {
      const fontSize = device === 'desktop' ? size.desktop : size.mobile;
      setStyle({
        fontSize: `${fontSize}rem`,
        lineHeight: '1.2',
      });
    }
  }, [device]);

  return (
    <div
      className='pre-title'
      style={style}
    >
      {text}
    </div>
  );
};

export const textWithImageFragment = graphql`
  fragment TextWithImageBlockFragment on Block {
    _template
    settings {
      ...SettingsFragment
    }
    rounded_corners
    background_color
    style
    pre_title
    title
    title_mobile
    pre_title_size {
      desktop
      mobile
    }
    text_background_color
    title_size {
      desktop
      mobile
    }
    text
    text_type
    image_position
    hide_image_on_mobile
    image_alt_text
    image
    cta_field {
      ...CtaFieldFragment
    }
    mobile_image_placement {
      placement
      max_width
    }
    rounded_corners_image
    text_image_block_width {
      desktop
      mobile
    }
    image_width {
      desktop
      mobile
    }
    text_with_blocks {
      blocks {
        _template
        title
        title_font_size
        text_font_size
        text
        icon
        cols
        list_columns
        submit_label
        items {
          title
          subtitle
          text
          icon_position
          icon
          url
        }
        head_columns {
          text
        }
        rows {
          text
          columns {
            text
          }
        }
        cta_position
        cta_text
        cta_url
        cta_subtext
        style
        columns_bootstrap_rule
        checkmarks_align_left
        margin_top
        margin_bottom
        checkmarks {
          name
          text
        }
      }
    }
  }
`;

export default TextWithImage;
